import { ApplicationEndpoints } from "src/generated/application";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { usePollingApi } from "src/platform/hooks/usePollingApi";

import type {
  BillingPlan,
  BillingForecast,
  FreeTierUsage,
  OrgBillingSummary,
  PaymentMethod,
  ProjectBillingSummary,
  StackBillingBreakdown,
} from "../types";

const BASE = apiBaseUrls.BACKEND;

export function useStackBilling(alias: string) {
  const url = `${BASE}${ApplicationEndpoints.GET_SYSTEM_BILLING.replace(":id", alias)}`;
  return usePollingApi<StackBillingBreakdown>(url, 60_000);
}

/** @deprecated Use useStackBilling */
export const useSystemBilling = useStackBilling;

export function useOrgBilling(orgAlias: string) {
  const url = `${BASE}${ApplicationEndpoints.GET_ORG_BILLING.replace(":id", orgAlias)}`;
  return usePollingApi<OrgBillingSummary>(url, 60_000);
}

export function useProjectBilling(projectAlias: string) {
  const url = `${BASE}${ApplicationEndpoints.GET_PROJECT_BILLING.replace(":id", projectAlias)}`;
  return usePollingApi<ProjectBillingSummary>(url, 60_000);
}

export interface BillingPlanResponse {
  plan: BillingPlan;
  quotaExhausted: boolean;
  freeTierUsage?: FreeTierUsage;
}

const FREE_PLAN_DEFAULT: BillingPlanResponse = {
  plan: "free",
  quotaExhausted: false,
  freeTierUsage: {
    activeStackCount: 0,
    stackLimit: 3,
    computeHoursUsed: 0,
    computeHoursLimit: 720,
  },
};

export function useBillingPlan() {
  const url = `${BASE}${ApplicationEndpoints.GET_BILLING_PLAN}`;
  const result = usePollingApi<BillingPlanResponse>(url, 120_000);

  // If the endpoint is not yet implemented (404) or unavailable, fall back to
  // a default free-plan response so the page is useful rather than broken.
  // isFallback=true signals that the data is synthetic — callers should hide
  // mutating actions (e.g. "Activate billing") that would also fail.
  if (result.error && !result.loading && result.data === null) {
    return {
      ...result,
      data: FREE_PLAN_DEFAULT,
      error: null,
      isFallback: true,
    };
  }

  return { ...result, isFallback: false };
}

export function useBillingForecast() {
  const url = `${BASE}${ApplicationEndpoints.GET_BILLING_FORECAST}`;
  return usePollingApi<BillingForecast>(url, 120_000);
}

export function usePaymentMethods() {
  const url = `${BASE}${ApplicationEndpoints.GET_PAYMENT_METHODS}`;
  return usePollingApi<PaymentMethod[]>(url, 120_000);
}

export function useFinOpsSummary() {
  const url = `${BASE}${ApplicationEndpoints.GET_FIN_OPS_SUMMARY}`;
  return usePollingApi<
    import("src/domains/organization/types/OrganizationTypes").FinOpsSummary
  >(url, 120_000);
}

export function useFinOpsByEnvironment() {
  const url = `${BASE}${ApplicationEndpoints.GET_FIN_OPS_BY_ENVIRONMENT}`;
  return usePollingApi<
    import("src/domains/organization/types/OrganizationTypes").CostByEnvironment[]
  >(url, 120_000);
}

export function useFinOpsRecommendations() {
  const url = `${BASE}${ApplicationEndpoints.GET_FIN_OPS_RECOMMENDATIONS}`;
  return usePollingApi<
    import("src/domains/organization/types/OrganizationTypes").FinOpsRecommendation[]
  >(url, 120_000);
}

export type {
  BillingPlan,
  FreeTierUsage,
  OrgBillingSummary,
  ProjectBillingSummary,
  StackBillingBreakdown,
  BillingForecast,
  PaymentMethod,
};
